import { useEffect, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useFormikContext } from "formik"
import { Alert, Stack, Typography } from "@mui/material"
import { compare } from "dinero.js"

import { DeliveryTimesField, DynamicConfigKey, MeetingServiceTabValues } from "../../form/formTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useTimeout } from "../../../../../../shared/hooks/useTimeout"
import { MeetingServiceTimeErrorDialog } from "./MeetingServiceTimeErrorDialog"
import { MeetingServicesPlaceholder } from "./MeetingServicesPlaceholder"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { toCurrency, toDinero, zeroDinero } from "../../../../../../modules/currency/currencyLib"
import { MeetingServiceDelivery } from "./MeetingServiceDelivery"
import { calculateTotalAmountFromDeliveries } from "./helpers"
import { INVALID_DELIVERIES } from "../../form/helpers"
import { OrderSummaryItem } from "./OrderSummaryItem"

const useStyles = makeStyles()((theme) => ({
    footerInfo: {
        fontWeight: 400,
        color: theme.palette.text.secondary,
    },
    errorAlert: {
        marginTop: "0.5rem",
    },
}))

const defaultDialogState = {
    show: false,
    times: [],
}

export type ProductUpdateQuantityProps = Readonly<{
    deliveryTime: string
    productId: number
    quantity: number
    isExtraService?: boolean
}>

type MeetingServicesProps = Readonly<{
    isRefundOrder: boolean
    isEditable: boolean
}>

export function MeetingServices({ isRefundOrder, isEditable }: MeetingServicesProps) {
    const moneyFactory = useMoney({})
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const { values, setFieldValue, errors, isSubmitting } = useFormikContext<MeetingServiceTabValues>()
    const deliveries: DeliveryTimesField | undefined = values[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const deliveryTimes = deliveries?.deliveryTimes ?? []
    const deliveryDate = deliveries?.deliveryDate ?? null
    const invalidTimes = errors[DynamicConfigKey.MULTIPLE_DELIVERIES]
    const initAmount = values.initAmount

    const [showInvalidTimesModal, setShowInvalidTimesModal] = useState<{ show: boolean; times: string[] }>(
        defaultDialogState,
    )
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    useEffect(() => {
        if (isSubmitting && typeof invalidTimes === "string" && invalidTimes.startsWith(INVALID_DELIVERIES)) {
            const times = invalidTimes.replace(INVALID_DELIVERIES, "").split(",") || []
            if (times.length > 0) {
                setShowInvalidTimesModal({
                    show: true,
                    times: times,
                })
            }
        }
    }, [isSubmitting, invalidTimes])

    // Auto-hide error message after 5 seconds
    useTimeout(() => setErrorMessage(null), errorMessage ? 5000 : null)

    const onUpdateProductQuantity = ({
        deliveryTime,
        productId,
        quantity,
        isExtraService,
    }: ProductUpdateQuantityProps) => {
        // Find current quantity before update
        let currentQuantity = 0
        const currentDelivery = deliveryTimes.find((delivery) => delivery.time === deliveryTime)
        if (currentDelivery) {
            const currentProduct = currentDelivery.orderLines.find(
                (item) => item.id === productId && item.isExtraService === isExtraService,
            )
            currentQuantity = currentProduct?.quantity ?? 0
        }

        const isQuantityIncreasing = quantity > currentQuantity

        const updatedDeliveries = {
            ...deliveries,
            deliveryTimes: deliveryTimes.map((delivery) => {
                if (delivery.time === deliveryTime) {
                    const current = delivery.orderLines.findIndex(
                        (item) => item.id === productId && item.isExtraService === isExtraService,
                    )
                    let newOrderLines = [...delivery.orderLines]
                    if (quantity > 0 || isRefundOrder) {
                        if (current !== -1) {
                            newOrderLines[current] = { ...newOrderLines[current], quantity }
                        }
                    } else {
                        newOrderLines = newOrderLines.filter(
                            (item) => !(item.id === productId && item.isExtraService === isExtraService),
                        )
                    }
                    return { ...delivery, orderLines: newOrderLines }
                }
                return delivery
            }),
        }

        // Validate that refund order total doesn't exceed initial amount when increasing quantity
        if (isRefundOrder && initAmount && deliveries && isQuantityIncreasing) {
            const newTotal = calculateTotalAmountFromDeliveries(updatedDeliveries.deliveryTimes, initAmount.currency)
            const initAmountDinero = toDinero(initAmount)

            // Check if new total is greater than initial amount
            if (compare(newTotal, initAmountDinero) > 0) {
                setErrorMessage(l10n.getString("create-order-refund-amount-exceeds-initial-error"))
                return
            }
        }

        // Clear error message when quantity is successfully updated
        setFieldValue(DynamicConfigKey.MULTIPLE_DELIVERIES, updatedDeliveries)
    }

    const totalAmount = useMemo(
        () => (deliveries ? calculateTotalAmountFromDeliveries(deliveries.deliveryTimes) : null),
        [deliveryTimes],
    )

    const isTotalAmountZero = useMemo(() => {
        if (!totalAmount) return true
        const zero = zeroDinero(toCurrency("DKK"))
        return compare(totalAmount, zero) === 0
    }, [totalAmount])

    const formattedDate = deliveryDate && dateTimeFactory.fromISO(deliveryDate).toFormat("d MMMM")
    const totalProductsCount = deliveryTimes.reduce((sum, delivery) => sum + delivery.orderLines.length, 0)
    const hasProducts = totalProductsCount > 0

    const handleDialogClose = () => {
        if (showInvalidTimesModal.times.length > 0) {
            const firstInvalidTime = showInvalidTimesModal.times[0]
            setFieldValue(`${DynamicConfigKey.MULTIPLE_DELIVERIES}.activeTime`, firstInvalidTime)
        }
        setShowInvalidTimesModal(defaultDialogState)
    }

    return (
        <LocalizedStrict id="create-order-order-summary-meeting-services" attrs={{ title: true }}>
            <OrderSummaryItem title="Meeting services">
                <Stack gap="1.5rem">
                    {formattedDate ? (
                        deliveryTimes.map((delivery, index) => (
                            <MeetingServiceDelivery
                                key={index}
                                serialNumber={index + 1}
                                deliveryTime={delivery.time || ""}
                                deliveryDate={deliveryDate}
                                orderLines={delivery.orderLines}
                                isRefundOrder={isRefundOrder}
                                onUpdateQuantity={onUpdateProductQuantity}
                                isEditable={isEditable}
                            />
                        ))
                    ) : (
                        <MeetingServicesPlaceholder />
                    )}
                    {hasProducts && (
                        <Stack gap="0.5rem">
                            {totalAmount && (
                                <Stack
                                    flexDirection="row"
                                    gap="1rem"
                                    justifyContent="space-between"
                                    alignItems="flex-end"
                                >
                                    <LocalizedStrict id="create-order-order-summary-meeting-services-total">
                                        <Typography variant="h6" className={classes.footerInfo}>
                                            Total
                                        </Typography>
                                    </LocalizedStrict>
                                    <Typography variant="h4">
                                        {isRefundOrder && !isTotalAmountZero && "-"}
                                        {moneyFactory.format(totalAmount)}
                                    </Typography>
                                </Stack>
                            )}
                            {errorMessage && (
                                <Alert
                                    severity="error"
                                    className={classes.errorAlert}
                                    onClose={() => setErrorMessage(null)}
                                >
                                    {errorMessage}
                                </Alert>
                            )}
                        </Stack>
                    )}

                    {showInvalidTimesModal.show && (
                        <MeetingServiceTimeErrorDialog
                            show={showInvalidTimesModal.show}
                            times={showInvalidTimesModal.times}
                            handleDialogClose={handleDialogClose}
                        />
                    )}
                </Stack>
            </OrderSummaryItem>
        </LocalizedStrict>
    )
}
